import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Calendar, formatDate, type PlainDate } from '../Calendar/Calendar.js';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useOutsideClick } from '../../hooks/useOutsideClick.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';
import { computePlacement, ensurePortalRoot, fixedStyle } from '../shared/index.js';
import type { PlacedRect } from '../shared/index.js';

export interface DatePickerProps {
  value?: PlainDate | null;
  defaultValue?: PlainDate | null;
  onChange?: (value: PlainDate | null) => void;
  placeholder?: string;
  clearLabel?: string;
  dialogLabel?: string;
  locale?: string;
  /** Min / max passed through to Calendar. */
  min?: PlainDate;
  max?: PlainDate;
  isDateDisabled?: (d: PlainDate) => boolean;
  weekStartsOn?: number;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Clear button inside the input. Default true. */
  clearable?: boolean;
  'aria-label'?: string;
  className?: string;
}

/** Input + popover Calendar. Renders a standard Input-like trigger
 *  showing the formatted date; clicking opens a portalled Calendar
 *  underneath. Outside-click and Escape close the popover; choosing a
 *  date commits and closes. */
export function DatePicker(props: DatePickerProps): ReactNode {
  const {
    value,
    defaultValue,
    onChange,
    placeholder,
    clearLabel = 'Clear',
    dialogLabel = 'Choose a date',
    locale,
    min,
    max,
    isDateDisabled,
    weekStartsOn = 0,
    disabled = false,
    invalid,
    size = 'md',
    clearable = true,
    className,
  } = props;

  const [current, setCurrent] = useControllableState<PlainDate | null>({
    defaultValue: defaultValue ?? null,
    ...(value !== undefined ? { value } : {}),
    ...(onChange !== undefined ? { onChange } : {}),
  });

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PlacedRect | null>(null);
  const portalRoot = useMemo(
    () => (typeof document === 'undefined' ? null : ensurePortalRoot()),
    [],
  );

  const resolvedLocale =
    locale ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

  useLayoutEffect(() => {
    if (!open || triggerRef.current === null) return undefined;
    const compute = (): void => {
      const trigger = triggerRef.current;
      const pop = popRef.current;
      if (trigger === null) return;
      const r = trigger.getBoundingClientRect();
      // Use the popover's actual size when available so flip + clamp
      // calculations are tight; on the very first paint the element is
      // parked off-screen so we estimate to avoid an obvious jump.
      const p = pop !== null ? pop.getBoundingClientRect() : { width: r.width, height: 320 };
      setPos(
        computePlacement(
          { top: r.top, left: r.left, width: r.width, height: r.height },
          { width: p.width, height: p.height },
          { placement: 'bottom-start', gap: 4, padding: 8, flip: true },
        ),
      );
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open]);

  useOutsideClick(
    popRef,
    useCallback(() => {
      setOpen(false);
    }, []),
    { enabled: open, extraRefs: [triggerRef] },
  );

  // Escape via the shared modal stack — only the topmost layer responds.
  useEscapeStack(
    useCallback(() => {
      setOpen(false);
      triggerRef.current?.focus();
    }, []),
    open,
  );

  const onPick = useCallback(
    (v: PlainDate | null) => {
      if (v === null || Array.isArray(v)) return;
      setCurrent(v);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [setCurrent],
  );

  const classes = [
    'vq-date-picker',
    `vq-date-picker--${size}`,
    disabled && 'vq-date-picker--disabled',
    invalid === true && 'vq-date-picker--invalid',
    open && 'vq-date-picker--open',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={classes}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={props['aria-label']}
        aria-invalid={invalid}
        data-component="DatePicker"
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className="vq-date-picker__value">
          {current === null ? (
            <span className="vq-date-picker__placeholder">{placeholder ?? 'Pick a date'}</span>
          ) : (
            formatDate(current, resolvedLocale)
          )}
        </span>
        {clearable && current !== null && !disabled && (
          <span
            className="vq-date-picker__clear"
            role="button"
            aria-label={clearLabel}
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              setCurrent(null);
            }}
          >
            ✕
          </span>
        )}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
          className="vq-date-picker__glyph"
        >
          <rect
            x="2"
            y="3"
            width="10"
            height="9"
            rx="1"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
          />
          <line x1="2" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5" />
          <line
            x1="5"
            y1="2"
            x2="5"
            y2="4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <line
            x1="9"
            y1="2"
            x2="9"
            y2="4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {open &&
        portalRoot !== null &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-label={dialogLabel}
            className="vq-date-picker__pop"
            data-side={pos?.side ?? 'bottom'}
            // z-index is set via the `.vq-date-picker__pop` CSS rule
            // (`var(--vq-z-popover)` — sits above Dialog). Removing
            // the inline `zIndex: var(--vq-z-dropdown)` override that
            // previously buried the calendar behind any modal that
            // triggered it; inline styles win over class CSS, and the
            // inline value (100) was below dialog (500).
            style={
              pos !== null ? fixedStyle(pos) : { position: 'fixed', top: -9999, left: -9999 }
            }
          >
            <Calendar
              selection="single"
              value={current}
              onChange={(v) => {
                onPick(v as PlainDate | null);
              }}
              {...(locale !== undefined ? { locale } : {})}
              weekStartsOn={weekStartsOn}
              {...(min !== undefined ? { min } : {})}
              {...(max !== undefined ? { max } : {})}
              {...(isDateDisabled !== undefined ? { isDateDisabled } : {})}
            />
          </div>,
          portalRoot,
        )}
    </>
  );
}
